import { z } from 'zod';

import { log } from '~/utils/logging';

/**
 * Shape guards for the raw, snake_case data sync responses.
 *
 * They check only what the code that follows dereferences: that a task or job
 * is an object with an id, and that a paginated response really carries a list.
 * Validating the full task here would defeat the purpose, because the API
 * accepts configs this app's schema does not model (see `authStepSchema`), and
 * rejecting those would make tasks unopenable rather than degrade. The id is
 * checked for presence only, not for uuid-ness, for the same reason.
 */

/**
 * A response that cannot be worked with. Its own class so the query client can
 * tell it apart from a network failure: retrying a malformed response only
 * repeats it.
 */
export class DataSyncResponseShapeError extends Error {}
export const rawEntityResponseSchema = z.looseObject({ id: z.string().min(1) });

export const rawPaginatedResponseSchema = z.looseObject({
  data: z.array(z.record(z.string(), z.unknown())),
});

/**
 * Validates a response against a shape guard, logging and failing the query or
 * mutation when it does not hold. Nothing is cast: a response that does not
 * match cannot be handed on as if it did.
 */
export function parseDataSyncResponse<T extends z.ZodType>(
  schema: T,
  response: unknown,
  what: string,
): z.infer<T> {
  const validated = schema.safeParse(response);

  if (validated.success) {
    return validated.data;
  }

  const message = `Data sync ${what} response has an unexpected shape`;

  log.warn({
    message,
    data: {
      errorMessage: validated.error.message,
      errors: validated.error.issues,
    },
  });

  throw new DataSyncResponseShapeError(message);
}
